/**
 * AtlasWelcomeModule — the PRE-ALPHA welcome window (2026-09-10): every time
 * a player joins Cosmos (once per page load, the moment the loading screen
 * drops) a draggable window says what Cosmos is, that this is a pre-alpha
 * build and not the final game, what the starter kit in their inventory is
 * for, and offers the Help & Manual and the Crafting Guide. Skipped under
 * automation (`navigator.webdriver`) unless `?welcome=1` — a probe that wants
 * it asks for it. Pure client-side.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';

export const WELCOME_COPY = {
  title: 'Welcome to COSMOS',
  prealpha: 'PRE-ALPHA BUILD — this is not the final game.',
  what: 'Cosmos is a persistent social multiverse: one shared Commons world of settlements, wilderness and a living NPC civilization, your own personal worlds, and a galaxy of solar systems above. Everything you own, earn and build is server-authoritative and carried by your Cosmos Passport.',
  expect: 'Expect missing art, placeholder shapes, bugs, balance swings and occasional resets while the systems are built out. Nothing here is final — not the visuals, not the economy, not the rules.',
  kit: 'Your starter kit is already in your inventory (F1): a vibro knife and a scrap pistol for the wilds, a field tool for sampling resources, a packed Field Extractor Rig deed to place on a resource hotspot, a Dust Skiff to ride (X), and a Lancer in your hangar for space travel.',
  next: 'Open the Workbench (F6) to survey and craft, the Crafting Guide (F10) to see everything you can make and how, and the field manual (H) for controls and every system.',
} as const;

export class AtlasWelcomeModule implements IModule {
  readonly name = 'AtlasWelcome';
  private win: UiWindow | null = null;
  private shownThisLoad = false;

  init(_app: App): void {
    void _app;
    bus.on('atlas:loading-done', () => this.maybeShow());
    (window as unknown as Record<string, unknown>).__welcome = {
      open: () => this.show(),
      close: () => this.win?.setOpen(false),
      isOpen: () => !!this.win && this.win.isOpen(),
      shown: () => this.shownThisLoad,
    };
  }

  private automated(): boolean {
    try {
      const forced = new URLSearchParams(location.search).get('welcome') === '1';
      return !!(navigator as { webdriver?: boolean }).webdriver && !forced;
    } catch { return false; }
  }

  private maybeShow(): void {
    // Once per page load — a world hop or a reconnect resume is not a new
    // join. Skipped under automation unless the probe asks for it.
    if (this.shownThisLoad || this.automated()) return;
    if (atlas.currentWorldId === null) return;
    this.shownThisLoad = true;
    this.show();
  }

  private show(): void {
    if (!this.win) this.build();
    this.shownThisLoad = true;
    this.win!.setOpen(true);
  }

  private build(): void {
    const win = makeWindow({
      id: 'welcome', title: WELCOME_COPY.title, icon: 'globe',
      width: 560, minWidth: 380, minHeight: 200,
      x: Math.max(12, (window.innerWidth - 560) / 2), y: Math.max(40, window.innerHeight * 0.16),
    });
    win.root.id = 'welcome-window';
    const body = document.createElement('div');
    body.style.cssText = 'padding:14px 16px 16px;font:13px/1.5 var(--ax-font);color:var(--ax-text);display:flex;flex-direction:column;gap:10px';
    const badge = document.createElement('div');
    badge.id = 'welcome-prealpha';
    badge.textContent = WELCOME_COPY.prealpha;
    badge.style.cssText = 'font:700 11px var(--ax-mono);letter-spacing:1.2px;color:var(--ax-hi);background:color-mix(in srgb, color-mix(in srgb, var(--ax-warn) 22%, var(--ax-bg2)) 55%, transparent);' +
      'border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:6px 10px';
    body.appendChild(badge);
    for (const [id, text] of [['welcome-what', WELCOME_COPY.what], ['welcome-expect', WELCOME_COPY.expect], ['welcome-kit', WELCOME_COPY.kit], ['welcome-next', WELCOME_COPY.next]] as const) {
      const p = document.createElement('p');
      p.id = id;
      p.textContent = text;
      p.style.margin = '0';
      if (id === 'welcome-what') p.style.color = 'var(--ax-text)';
      body.appendChild(p);
    }
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;margin-top:4px';
    const mk = (id: string, label: string, fn: () => void, primary = false): HTMLButtonElement => {
      const b = document.createElement('button');
      b.id = id;
      b.textContent = label;
      b.style.cssText = primary
        ? 'cursor:pointer;background:var(--ax-sel);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:6px 14px;font:600 12px var(--ax-font)'
        : 'cursor:pointer;background:var(--ax-bg3);color:var(--ax-hi);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:6px 14px;font:600 12px var(--ax-font)';
      b.addEventListener('click', fn);
      return b;
    };
    row.appendChild(mk('welcome-help', 'Open the field manual (H)', () => { bus.emit('atlas:open-help', {}); }, true));
    row.appendChild(mk('welcome-guide', 'Crafting Guide (F10)', () => { bus.emit('atlas:open-craft-guide', {}); }));
    row.appendChild(mk('welcome-inventory', 'Inventory (F1)', () => { bus.emit('atlas:open-workbench', { tab: 'inventory' }); }));
    row.appendChild(mk('welcome-begin', 'Begin', () => win.setOpen(false)));
    body.appendChild(row);
    win.body.appendChild(body);
    this.win = win;
  }

  dispose(): void { this.win?.root.remove(); this.win = null; }
}
